import React, { useEffect, useState } from 'react';
import { 
  XMarkIcon, 
  ExclamationTriangleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const WithdrawApplicationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  courseName, 
  registrationId,
  isLoading = false,
  error = ''
}) => {
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!isOpen) setConfirmed(false);
  }, [isOpen]);

  const handleConfirm = () => {
    if (confirmed) {
      onConfirm(registrationId);
    }
  };

  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="withdraw-dialog-title"
        style={{
        backgroundColor: 'var(--white)',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 0 24px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#FEF3C7',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ExclamationTriangleIcon style={{
                width: '24px',
                height: '24px',
                color: '#F59E0B'
              }} />
            </div>
            <div>
              <h3 id="withdraw-dialog-title" style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                margin: 0
              }}>
                Withdraw Application
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--text-gray)',
                margin: '4px 0 0 0'
              }}>
                This action cannot be undone
              </p>
            </div>
          </div>
          
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              color: 'var(--text-gray)',
              transition: 'all 0.2s ease',
              opacity: isLoading ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = 'var(--background-gray)';
                e.target.style.color = 'var(--primary-black)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'var(--text-gray)';
              }
            }}
          >
            <XMarkIcon style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px 24px 24px' }}>
          <div style={{
            padding: '20px',
            backgroundColor: '#FEF3C7',
            borderRadius: '12px',
            border: '1px solid #FCD34D',
            marginBottom: '24px'
          }}>
            <p style={{
              fontSize: '16px',
              color: 'var(--primary-black)',
              margin: 0,
              lineHeight: 1.6
            }}>
              Are you sure you want to withdraw your application for{' '}
              <strong>{courseName}</strong>?
            </p>
            <ul style={{
              margin: '16px 0 0 0',
              paddingLeft: '20px',
              color: 'var(--text-gray)',
              fontSize: '14px',
              lineHeight: 1.6
            }}>
              <li>Your application will be permanently removed</li>
              <li>You will need to reapply if you change your mind</li>
              <li>Your position in the application queue will be lost</li>
            </ul>
          </div>

          {/* Confirmation Checkbox */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: 'var(--background-gray)',
            borderRadius: '8px'
          }}>
            <input
              type="checkbox"
              id="withdraw-confirm"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={isLoading}
              style={{
                width: '18px',
                height: '18px',
                marginTop: '2px',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            />
            <label 
              htmlFor="withdraw-confirm"
              style={{
                fontSize: '14px',
                color: 'var(--primary-black)',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                lineHeight: 1.5
              }}
            >
              I understand that withdrawing my application is permanent and cannot be undone
            </label>
          </div>

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: '20px',
                padding: '12px 14px',
                border: '1px solid #f2b8bf',
                borderRadius: '9px',
                backgroundColor: '#fff4f5',
                color: '#9d2532',
                fontSize: '14px',
                lineHeight: 1.5
              }}
            >
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={handleClose}
              disabled={isLoading}
              style={{
                padding: '12px 20px',
                borderRadius: '8px',
                border: '1px solid var(--border-gray)',
                backgroundColor: 'var(--white)',
                color: 'var(--text-gray)',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isLoading ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.backgroundColor = 'var(--background-gray)';
                  e.target.style.color = 'var(--primary-black)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.backgroundColor = 'var(--white)';
                  e.target.style.color = 'var(--text-gray)';
                }
              }}
            >
              Cancel
            </button>
            
            <button
              onClick={handleConfirm}
              disabled={!confirmed || isLoading}
              style={{
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: confirmed && !isLoading ? '#DC2626' : '#E5E7EB',
                color: confirmed && !isLoading ? 'white' : '#9CA3AF',
                fontSize: '14px',
                fontWeight: '600',
                cursor: confirmed && !isLoading ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (confirmed && !isLoading) {
                  e.target.style.backgroundColor = '#B91C1C';
                }
              }}
              onMouseLeave={(e) => {
                if (confirmed && !isLoading) {
                  e.target.style.backgroundColor = '#DC2626';
                }
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid currentColor',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Withdrawing...
                </>
              ) : (
                <>
                  <XCircleIcon style={{ width: '16px', height: '16px' }} />
                  Withdraw Application
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawApplicationModal;
